import { Component, HostListener, OnInit } from '@angular/core';
import { ComponentHasUnsavedChanges } from '../../../../../../../unsaved-changes-guard.service';
import { Membership, MembershipCategory } from '../../../../../../../models/membership';
import { MyAccount } from '../../../../../../../models/my-account';
import { MembershipPurchaseType, MembershipStatus } from '../../../../../../../models/membership-enums';
import {
  OrganizationConfigService,
} from '../../../../../../../services/organization-config/organization-config.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MembershipService } from '../../../../../../../services/membership/membership.service';
import { AccountService } from '../../../../../../../services/account/account.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  SimpleMessageModalComponent,
} from '../../../../../../../components/modals/simple-message-modal/simple-message-modal.component';
import { getUrlHashDerivedFromText } from '../../../../../../../utils';


@Component({
  selector: 'app-membership-category-detail',
  templateUrl: './membership-category-detail.component.html',
  styleUrls: ['./membership-category-detail.component.css']
})
export class MembershipCategoryDetailComponent implements OnInit, ComponentHasUnsavedChanges {
  public isNewCategory: boolean;
  public category: MembershipCategory = {
    id: null,
    url: null,
    name: '',
    url_hash: '',
    assigned_memberships: [],
    assigned_membership_ids: [],
    created_at: null,
    updated_at: null
  };
  public memberships: Membership[];

  // Used to ensure retired memberships are included as options if they are
  // already assigned to the category.
  // Otherwise, retired memberships are not shown.
  public initialMembershipIds: number[] = [];

  public errorMessage = '';
  public errors: any = {};

  public urlHashEdited = false;

  public loadingExistingCategory = false;
  public loadingMemberships = false;
  public loadingSubmit = false;
  public loadingDelete = false;

  public categoryNotFound = false;
  public serverError: number = null;
  public unsavedChanges = false;

  public myAccount: MyAccount;

  public MembershipStatus = MembershipStatus;

  constructor(
    public orgConfig: OrganizationConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private membershipService: MembershipService,
    private accountService: AccountService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    this.isNewCategory = !routeParams.hasOwnProperty('id');

    this.myAccount = this.accountService.getMyAccount();
    if (!this.myAccount.is_organization_admin) { return; }

    // Get the category to edit, if an ID was provided.
    if (!this.isNewCategory) {
      const idInt = parseInt(routeParams.id, 10);
      if (isNaN(idInt)) {
        this.categoryNotFound = true;
        this.loadingExistingCategory = false;
        return;
      }

      this.loadCategory(idInt);

      this.urlHashEdited = true;
    }

    this.loadMemberships();
  }

  hasUnsavedChanges() {
    return this.unsavedChanges;
  }

  @HostListener('window:beforeunload')
  allowUnload() {
    return !this.hasUnsavedChanges();
  }

  public loadCategory(id: number) {
    this.loadingExistingCategory = true;

    this.membershipService
      .getMembershipCategory(id)
      .then(category => {
        this.category = category;
        this.category.assigned_membership_ids = this.category.assigned_memberships.map(am => am.membership_id);
        this.initialMembershipIds = [...this.category.assigned_membership_ids];
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.categoryNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingExistingCategory = false; });
  }

  public loadMemberships() {
    this.loadingMemberships = true;

    this.membershipService
      .getMemberships(1, 3000)
      .then(result => {
        this.memberships = result.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingMemberships = false; });
  }

  public handleSubmit() {
    if (this.loadingSubmit) { return; }

    this.loadingSubmit = true;

    this.errorMessage = '';
    this.errors = {};

    let savePromise: Promise<MembershipCategory>;
    if (this.isNewCategory) {
      savePromise = this.membershipService.addMembershipCategory(this.category);
    } else {
      savePromise = this.membershipService.updateMembershipCategory(this.category);
    }

    savePromise
      .then(savedCategory => {
        this.category = savedCategory;

        this.unsavedChanges = false;

        let successMessage: string;
        if (this.isNewCategory) {
          successMessage = this.category.name + ' created successfully.';
        } else {
          successMessage = this.category.name + ' updated successfully.';
        }

        this.router.navigate(
          ['/membership-settings/categories'],
          {
            state: {
              feedback: {
                text: successMessage,
                status: 'success'
              }
            }
          }
        );
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors shown and try again.';
        } else if (reason.status === 403) {
          if (this.isNewCategory) {
            this.errorMessage = 'You do not have permission to add membership categories.';
          } else {
            this.errorMessage = 'You do not have permission to modify this category.';
          }
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  public handleDelete() {
    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Delete Category';
    modalRef.componentInstance.messageText = 'Are you sure you want to delete this category? This action cannot be undone.';
    modalRef.componentInstance.messageAlertClass = 'alert-danger';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.loadingDelete = true;

      this.membershipService
        .deleteMembershipCategory(this.category)
        .then(() => {
          this.unsavedChanges = false;

          this.router.navigate(
            ['/membership-settings/categories'],
            {
              state: {
                feedback: {
                  text: this.category.name + ' deleted.',
                  status: 'success'
                }
              }
            }
          )
        })
        .catch(reason => {
          if (reason.status === 400) {
            this.errors = reason.error;
            this.errorMessage = 'Please correct the errors shown and try again.';
          } else if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to delete this category.';
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingDelete = false; });
    }
  }

  public getFilteredMemberships(): Membership[] {
    return this.memberships.filter(m => {
      if (m.purchase_type === MembershipPurchaseType.benefit) { return false; }

      if ([MembershipStatus.draft, MembershipStatus.active].includes(m.status)) {
        return true;
      }

      // Allow retired memberships if they are already assigned.
      if (this.initialMembershipIds.includes(m.id)) { return true; }

      return false;
    })
  }

  public membershipSelected(membership: Membership) {
    return this.category.assigned_membership_ids.includes(membership.id);
  }

  public toggleMembership(membership: Membership) {
    if (this.membershipSelected(membership)) {
      this.category.assigned_membership_ids.splice(this.category.assigned_membership_ids.indexOf(membership.id), 1);
    } else {
      this.category.assigned_membership_ids.push(membership.id);
    }
  }

  public allMembershipsAreSelected() {
    for (const et of this.getFilteredMemberships()) {
      if (!this.category.assigned_membership_ids.includes(et.id)) {
        return false;
      }
    }

    return true;
  }

  public setNoMembershipsSelected() {
    this.category.assigned_membership_ids = [];
  }

  public setAllMembershipsSelected() {
    this.category.assigned_membership_ids = [];
    for (const et of this.getFilteredMemberships()) {
      this.category.assigned_membership_ids.push(et.id);
    }
  }

  public isLoading() {
    return (
      this.loadingExistingCategory ||
      this.loadingSubmit ||
      this.loadingDelete
    );
  }

  public handleNameChange() {
    if (!this.urlHashEdited) {
      this.category.url_hash = getUrlHashDerivedFromText(this.category.name);
    }
  }

  public handleUrlHashChange() { this.urlHashEdited = true; }
}
